/**
 * Print layouts: the weekly status note and the monthly timesheet.
 *
 * Both are built as HTML into #printRoot, which is hidden on screen and shown
 * only by the print stylesheet. Printing is the same action as "save as PDF" in
 * the macOS and iOS print dialogs, so this covers both.
 */

import { escapeHtml } from './dom.js';
import { formatDate, formatMonth, startOfWeek, weekDays } from './dates.js';
import {
  BILLABLE_DAY_TYPES,
  billableValue,
  daysOpen,
  doneSummary,
  formatDays,
  formatKm,
  formatMoney,
  isBillable,
  locationLabel,
  openWaitingOn,
} from './model.js';

const root = () => document.getElementById('printRoot');

/** Put `html` into the print container and open the print dialog. */
function printDocument(html) {
  const container = root();
  container.innerHTML = html;
  // Let the browser lay the new content out before the dialog takes a snapshot.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => window.print());
  });
}

/** Clear the print container once the dialog closes, so nothing lingers. */
window.addEventListener('afterprint', () => {
  const container = root();
  if (container) container.innerHTML = '';
});

const esc = escapeHtml;

function listOrEmpty(items, emptyText) {
  if (!items.length) return `<p class="print-empty">${esc(emptyText)}</p>`;
  return `<ul>${items.join('')}</ul>`;
}

// --- Weekly status note -----------------------------------------------------

/**
 * "Weekly update, week commencing DD MMM YYYY".
 *
 * Deliberately carries no company details: this goes to the client as a note,
 * not as an invoice document.
 */
export function printStatusNote({ weekStart, days, settings }) {
  const monday = startOfWeek(weekStart);
  const dates = weekDays(monday);
  const inWeek = days.filter((d) => dates.includes(d.date));

  // Done this week, grouped by workstream in the order settings lists them.
  const byWorkstream = new Map();
  for (const day of inWeek) {
    for (const entry of day.done) {
      if (!entry.text.trim()) continue;
      const key = entry.workstream || 'Unassigned';
      if (!byWorkstream.has(key)) byWorkstream.set(key, []);
      byWorkstream.get(key).push({ text: entry.text.trim(), date: day.date });
    }
  }
  const order = [...settings.workstreams, ...[...byWorkstream.keys()].filter((k) => !settings.workstreams.includes(k))];

  const doneSections = order
    .filter((ws) => byWorkstream.has(ws))
    .map((ws) => {
      const items = byWorkstream
        .get(ws)
        .map((item) => `<li>${esc(item.text)} <span class="meta">(${esc(formatDate(item.date))})</span></li>`)
        .join('');
      return `<h3>${esc(ws)}</h3><ul>${items}</ul>`;
    })
    .join('');

  // Next week: planned items still open at the end of the week.
  const nextItems = [];
  for (const day of inWeek) {
    for (const entry of day.planned) {
      if (entry.done || !entry.text.trim()) continue;
      nextItems.push(entry);
    }
  }
  // The same item is carried from day to day, so collapse duplicates by text.
  const seen = new Set();
  const nextHtml = nextItems
    .filter((entry) => {
      const key = `${entry.workstream}|${entry.text.trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(
      (entry) =>
        `<li>${esc(entry.text.trim())}${entry.workstream ? ` <span class="meta">— ${esc(entry.workstream)}</span>` : ''}</li>`,
    );

  const waiting = openWaitingOn(inWeek).map((item) => {
    const age = daysOpen(item, dates[6]);
    return `<li><strong>${esc(item.who || 'Unassigned')}</strong> — ${esc(item.what)} <span class="meta">(open ${age} ${age === 1 ? 'day' : 'days'}, since ${esc(formatDate(item.sinceDate))})</span></li>`;
  });

  const html = `
    <article class="print-doc">
      <h1>Weekly update, week commencing ${esc(formatDate(monday))}</h1>
      <p class="print-sub">${esc(formatDate(dates[0]))} to ${esc(formatDate(dates[6]))}${
        settings.clientName ? ` · ${esc(settings.clientName)}` : ''
      }</p>

      <h2>Done this week</h2>
      ${doneSections || '<p class="print-empty">Nothing recorded this week.</p>'}

      <h2>Next week</h2>
      ${listOrEmpty(nextHtml, 'Nothing planned yet.')}

      <h2>Waiting on</h2>
      ${listOrEmpty(waiting, 'Nothing outstanding.')}
    </article>
  `;

  printDocument(html);
}

// --- Monthly timesheet ------------------------------------------------------

/**
 * The client-facing timesheet for a month: billable days, totals, expenses and
 * a signature line for approval.
 */
export function printTimesheet({ monthKey, days, settings }) {
  const billableDays = days
    .filter((day) => isBillable(day))
    .sort((a, b) => a.date.localeCompare(b.date));

  const rows = billableDays
    .map((day) => {
      const summary = doneSummary(day) || '—';
      return `<tr>
        <td>${esc(formatDate(day.date))}</td>
        <td>${esc(day.dayType)}</td>
        <td>${esc(locationLabel(day))}</td>
        <td>${esc(summary)}</td>
        <td class="num">${esc(formatDays(billableValue(day)))}</td>
      </tr>`;
    })
    .join('');

  const totalDays = billableDays.reduce((sum, day) => sum + billableValue(day), 0);
  const fullCount = billableDays.filter((d) => d.dayType === 'Full').length;
  const halfCount = billableDays.filter((d) => d.dayType === 'Half').length;

  const expenses = [];
  for (const day of days) {
    for (const expense of day.expenses) {
      expenses.push({ ...expense, date: day.date });
    }
  }
  expenses.sort((a, b) => a.date.localeCompare(b.date));

  const expenseTotal = expenses.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
  const kmTotal = expenses.reduce((sum, x) => sum + (Number(x.km) || 0), 0);

  const expenseRows = expenses
    .map(
      (x) => `<tr>
        <td>${esc(formatDate(x.date))}</td>
        <td>${esc(x.category || '—')}</td>
        <td>${esc(x.note || '')}</td>
        <td class="num">${x.km === '' || x.km === null ? '' : esc(formatKm(x.km))}</td>
        <td>${esc(x.receiptRef || '')}</td>
        <td class="num">${esc(formatMoney(x.amount))}</td>
      </tr>`,
    )
    .join('');

  const expenseSection = expenses.length
    ? `<table class="print-table">
        <thead>
          <tr><th>Date</th><th>Category</th><th>Note</th><th class="num">Distance</th><th>Receipt ref.</th><th class="num">Amount</th></tr>
        </thead>
        <tbody>${expenseRows}</tbody>
        <tfoot>
          <tr>
            <td colspan="3">Total</td>
            <td class="num">${kmTotal ? esc(formatKm(kmTotal)) : ''}</td>
            <td></td>
            <td class="num">${esc(formatMoney(expenseTotal))}</td>
          </tr>
        </tfoot>
      </table>`
    : '<p class="print-empty">No expenses recorded for this month.</p>';

  const meta = [
    ['Consultant', settings.consultantName],
    ['Client', settings.clientName],
    ['Company', settings.companyName],
    ['Client contact', settings.clientContact],
    ['Company number', settings.companyNumber],
    ['Period', formatMonth(monthKey)],
  ]
    .filter(([, value]) => String(value || '').trim())
    .map(([key, value]) => `<div><span class="k">${esc(key)}</span><span class="v">${esc(value)}</span></div>`)
    .join('');

  const html = `
    <article class="print-doc">
      <h1>Timesheet — ${esc(formatMonth(monthKey))}</h1>
      <div class="print-meta">${meta}</div>

      <h2>Billable days</h2>
      ${
        billableDays.length
          ? `<table class="print-table">
              <thead>
                <tr><th>Date</th><th>Day type</th><th>Location</th><th>Work done</th><th class="num">Days</th></tr>
              </thead>
              <tbody>${rows}</tbody>
              <tfoot>
                <tr><td colspan="4">Total billable days</td><td class="num">${esc(formatDays(totalDays))}</td></tr>
              </tfoot>
            </table>
            <p class="print-totals">
              <strong>${fullCount}</strong> full ${fullCount === 1 ? 'day' : 'days'},
              <strong>${halfCount}</strong> half ${halfCount === 1 ? 'day' : 'days'},
              total <strong>${esc(formatDays(totalDays))}</strong> billable ${totalDays === 1 ? 'day' : 'days'}.
            </p>`
          : '<p class="print-empty">No billable days recorded for this month.</p>'
      }

      <h2>Expenses</h2>
      ${expenseSection}

      <div class="print-sign">
        <h2>Client approval</h2>
        <p>I confirm the days and expenses recorded above are a true record of work delivered.</p>
        <div class="print-sign__row">
          <div class="print-sign__line">
            <div class="print-sign__rule"></div>
            <div class="print-sign__caption">Signed${settings.clientContact ? ` — ${esc(settings.clientContact)}` : ''}</div>
          </div>
          <div class="print-sign__line">
            <div class="print-sign__rule"></div>
            <div class="print-sign__caption">Name in capitals</div>
          </div>
          <div class="print-sign__line">
            <div class="print-sign__rule"></div>
            <div class="print-sign__caption">Date</div>
          </div>
        </div>
      </div>

      <p class="print-foot">
        ${esc(settings.companyName || '')}${settings.companyNumber ? ` · Company number ${esc(settings.companyNumber)}` : ''}
        · Timesheet generated ${esc(formatDate(new Date().toISOString().slice(0, 10)))}
      </p>
    </article>
  `;

  printDocument(html);
}

export { BILLABLE_DAY_TYPES };
